import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WrapperHttpService } from '../../../services/wrapper-http-service';
import { API_URL } from '../../../../services/config';
import { DEFAULT_LIST_QUERY_PARAM_LIMIT } from '../../../../common/models';
import { ListQueryParam } from '../../../../common/constants/list-query-param.constant';
import {
  MonitoringFilterGateOption,
  MonitoringFilterPlantOption,
} from '../models/monitoring-filter-plant-option.model';
import { FilterOptionsEndpoint } from './constants';
import { getQueryStringFromObject } from '../../../../common/utils';

@Injectable({
  providedIn: 'root',
})
export class FilterOptionsService {
  private readonly checkboxFilterListQueryParamLimit = 5;
  private readonly businessUnitsListQueryParamLimit = 50;

  constructor(private http: WrapperHttpService) {}

  getInboundPlantOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.TransportManagement}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.Search
    }${FilterOptionsEndpoint.InboundPlants}?${getQueryStringFromObject({
      [ListQueryParam.Limit]: DEFAULT_LIST_QUERY_PARAM_LIMIT,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getPlantOptions(filter: string): Observable<MonitoringFilterPlantOption[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.TransportManagement}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.Search
    }${FilterOptionsEndpoint.Plants}?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.checkboxFilterListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<MonitoringFilterPlantOption[]>(url);
  }

  getGateOptions(filter: string): Observable<MonitoringFilterGateOption[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.TransportManagement}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.Search
    }${FilterOptionsEndpoint.Gates}?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.checkboxFilterListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<MonitoringFilterGateOption[]>(url);
  }

  getSapIdOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.AutomotiveAftermarket}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.SapIds
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.checkboxFilterListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getBusinessUnitsOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.BusinessUnits
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.businessUnitsListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getNetworkPartOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.NetworkParts
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.checkboxFilterListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getConsignorAddressOptions(filter: string): Observable<{ name: string; city: string; country: string }[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.Consignors
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: DEFAULT_LIST_QUERY_PARAM_LIMIT,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<{ name: string; city: string; country: string }[]>(url);
  }

  getFreightPayersOptions(filter: string): Observable<{ extId: string; city: string }[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.FreightPayers
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: DEFAULT_LIST_QUERY_PARAM_LIMIT,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<{ extId: string; city: string }[]>(url);
  }

  getSourceLocationsOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.SourceLocations
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: DEFAULT_LIST_QUERY_PARAM_LIMIT,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getDestinationLocationsOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.DestinationLocations
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: DEFAULT_LIST_QUERY_PARAM_LIMIT,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }

  getPortOfLoadingAndDischargeOptions(filter: string): Observable<{ abbreviation: string; cityName: string }[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Abbreviations}?${getQueryStringFromObject({
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<{ abbreviation: string; cityName: string }[]>(url);
  }

  getSapSystemsOptions(filter: string): Observable<string[]> {
    const url = `${API_URL}${FilterOptionsEndpoint.Shipments}${FilterOptionsEndpoint.Filters}${
      FilterOptionsEndpoint.SapSystems
    }?${getQueryStringFromObject({
      [ListQueryParam.Limit]: this.checkboxFilterListQueryParamLimit,
      [ListQueryParam.Filter]: filter,
    })}`;
    return this.http.withLoading().get<string[]>(url);
  }
}
